import { useEffect, useState } from "react";
import { Save, Eye, EyeOff, Send, KeyRound, AtSign, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

export default function AdminSettings() {
  useSeo({ title: "Settings — RSR Admin" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState({
    resend_api_key: "",
    resend_api_key_set: false,
    resend_api_key_preview: "",
    sender_email: "",
    sender_name: "",
    admin_recipient_email: "",
    send_customer_autoreply: true,
    send_admin_notification: true,
  });
  const [showKey, setShowKey] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testTpl, setTestTpl] = useState("customer");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setS(prev => ({ ...prev, ...data, resend_api_key: "" }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        sender_email: s.sender_email,
        sender_name: s.sender_name,
        admin_recipient_email: s.admin_recipient_email,
        send_customer_autoreply: s.send_customer_autoreply,
        send_admin_notification: s.send_admin_notification,
      };
      // Only send key if user typed a new one
      if (s.resend_api_key && s.resend_api_key.trim()) {
        payload.resend_api_key = s.resend_api_key.trim();
      }
      const { data } = await api.put("/admin/settings", payload);
      setS(prev => ({ ...prev, ...data, resend_api_key: "" }));
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error("Enter an email address to test to.");
      return;
    }
    try {
      await api.post("/admin/settings/test-email", { to: testTo.trim(), template: testTpl });
      toast.success(`Test email sent to ${testTo} — check your inbox.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="px-8 py-16 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-3xl" data-testid="admin-settings">
      <p className="overline">Configuration</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter">Settings</h1>
      <p className="mt-2 text-sm text-muted-foreground">Email delivery settings for the contact form. Changes save to the database and apply immediately to new submissions.</p>

      {/* Resend */}
      <section className="mt-10 border border-border bg-card p-7">
        <div className="flex items-center gap-3 mb-5">
          <KeyRound className="w-5 h-5 text-accent" />
          <h2 className="font-display text-xl font-bold tracking-tight">Resend API</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Get a key from <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-accent hover:underline">resend.com/api-keys</a>. Leave blank to keep your current key.</p>

        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">Resend API key</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                data-testid="settings-resend-key"
                type={showKey ? "text" : "password"}
                value={s.resend_api_key}
                onChange={(e) => setS({ ...s, resend_api_key: e.target.value })}
                placeholder={s.resend_api_key_set ? `Saved: ${s.resend_api_key_preview}` : "re_xxxxxxxxxxxxxxxx"}
                className="bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12 font-mono text-sm"
              />
              <button type="button" onClick={() => setShowKey(v => !v)} className="border border-border hover:border-foreground/40 p-3" title={showKey ? "Hide" : "Show"}>
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {s.resend_api_key_set && !s.resend_api_key && (
              <p className="mt-2 text-xs text-muted-foreground">A key is already saved. Leave blank to keep it, or paste a new one to replace.</p>
            )}
            {!s.resend_api_key_set && (
              <p className="mt-2 text-xs text-amber-600 inline-flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> No key configured yet — emails won't send until you add one.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="sender_name" className="text-xs uppercase tracking-[0.18em] text-foreground/70">Sender display name</Label>
              <Input
                id="sender_name"
                data-testid="settings-sender-name"
                value={s.sender_name || ""}
                onChange={(e) => setS({ ...s, sender_name: e.target.value })}
                placeholder="RSR Australia"
                className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
              />
            </div>
            <div>
              <Label htmlFor="sender_email" className="text-xs uppercase tracking-[0.18em] text-foreground/70">Sender email (from)</Label>
              <Input
                id="sender_email"
                type="email"
                data-testid="settings-sender-email"
                value={s.sender_email || ""}
                onChange={(e) => setS({ ...s, sender_email: e.target.value })}
                placeholder="noreply@rsraustralia.com.au"
                className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
              />
              <p className="mt-2 text-xs text-muted-foreground">Must be on a verified Resend domain. <code className="bg-secondary px-1.5 py-0.5">onboarding@resend.dev</code> works for testing without verification.</p>
            </div>
          </div>

          <div>
            <Label htmlFor="admin_recipient_email" className="text-xs uppercase tracking-[0.18em] text-foreground/70">Lead notification email (to)</Label>
            <Input
              id="admin_recipient_email"
              type="email"
              data-testid="settings-admin-recipient"
              value={s.admin_recipient_email || ""}
              onChange={(e) => setS({ ...s, admin_recipient_email: e.target.value })}
              placeholder="hello@rsraustralia.com.au"
              className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
            />
            <p className="mt-2 text-xs text-muted-foreground">Where new lead alerts get sent.</p>
          </div>
        </div>
      </section>

      {/* Toggles */}
      <section className="mt-6 border border-border bg-card p-7">
        <h2 className="font-display text-xl font-bold tracking-tight mb-5">What to send</h2>
        <div className="space-y-4">
          <ToggleRow
            label="Send a Thank You auto-reply to the customer"
            description="Confirmation email to the person who submitted the form."
            checked={s.send_customer_autoreply}
            onChange={v => setS({ ...s, send_customer_autoreply: v })}
            testid="settings-toggle-customer"
          />
          <ToggleRow
            label="Send a lead notification to the admin email"
            description="Internal alert with the lead details, sent to the address above."
            checked={s.send_admin_notification}
            onChange={v => setS({ ...s, send_admin_notification: v })}
            testid="settings-toggle-admin"
          />
        </div>
      </section>

      {/* Test email */}
      <section className="mt-6 border border-border bg-card p-7">
        <div className="flex items-center gap-3 mb-5">
          <Send className="w-5 h-5 text-accent" />
          <h2 className="font-display text-xl font-bold tracking-tight">Send a test email</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Sends a sample using the current templates and Resend config.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            data-testid="settings-test-to"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="sm:col-span-2 bg-background border-border h-12"
          />
          <select
            data-testid="settings-test-template"
            value={testTpl}
            onChange={(e) => setTestTpl(e.target.value)}
            className="bg-background border border-border h-12 px-3 text-sm"
          >
            <option value="customer">Customer template</option>
            <option value="admin">Admin template</option>
          </select>
        </div>
        <button
          onClick={sendTest}
          data-testid="settings-test-send"
          className="mt-4 inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-5 py-3 text-sm font-semibold"
        >
          <Send className="w-4 h-4" /> Send test
        </button>
      </section>

      {/* Save bar */}
      <div className="mt-8 flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3 font-semibold text-sm hover:bg-accent/90 transition-colors disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
        </button>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5" /> Email templates are edited on the next tab.</p>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-4 p-4 border border-border hover:border-foreground/30 transition-colors cursor-pointer">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-1" />
      <div>
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
      </div>
    </label>
  );
}
